/*
  Product page orchestrator.

  Responsibilities:
    1. Variant change → URL replaceState + multi-section re-fetch + swap
    2. Sticky mobile ATC bar IntersectionObserver wiring
    3. ATC submit interceptor with MugCart progressive enhancement
    4. ATC error → contextual notify-me surfacing with reset on next change

  Each variant-aware section ([data-product-section]) re-fetches in parallel.
  If a fetch fails for one section, the existing DOM stays in place for that
  section (per-section try/catch); we never let one bad response break the
  whole page. Failures log to console for debugging.
*/

(() => {
  // ─── Read embedded product JSON ──────────────────────────────────────
  const readProductJSON = () => {
    const el = document.querySelector('[data-product-json]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch { return null; }
  };

  // ─── Section Rendering API helpers ───────────────────────────────────
  const collectSectionIds = () =>
    [...document.querySelectorAll('[data-product-section]')]
      .map((el) => el.dataset.productSection)
      .filter(Boolean);

  const fetchSection = async (url, sectionId) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('sections', sectionId);
    const res = await fetch(u.toString(), { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Fetch ${sectionId} failed: ${res.status}`);
    const data = await res.json();
    return data[sectionId];
  };

  const swapSection = (sectionId, html) => {
    const current = document.querySelector(`[data-product-section="${sectionId}"]`);
    if (!current || !html) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const fresh = tmp.querySelector(`[data-product-section="${sectionId}"]`);
    if (!fresh) return;
    current.replaceWith(fresh);
  };

  // ─── Variant matching ────────────────────────────────────────────────
  const findVariantId = (productData) => {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return null;
    const checked = [...variantSelects.querySelectorAll('input[type="radio"]:checked')];
    if (checked.length === 0) return null;
    const selectedOptions = checked.map((r) => r.value);
    const match = productData.variants.find((v) =>
      selectedOptions.every((val, i) => v.options[i] === val)
    );
    return match?.id ?? null;
  };

  // ─── Notify-me ATC-error contextual heading ──────────────────────────
  const setNotifyErrorContext = (on) => {
    document.querySelectorAll('[data-notify-root]').forEach((root) => {
      root.classList.toggle('product-notify--atc-error', !!on);
    });
  };

  // ─── Variant change → re-render ──────────────────────────────────────
  let inflight = null;

  const handleVariantChange = async () => {
    const productData = readProductJSON();
    if (!productData) return;

    const variantId = findVariantId(productData);
    if (!variantId) return;

    // Update URL (replaceState — back button doesn't break per spec)
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());

    // Reset ATC-error context — user is exploring variants, not failing ATC.
    setNotifyErrorContext(false);

    const sectionIds = collectSectionIds();
    const fetchUrl = `${productData.url}?variant=${variantId}`;

    inflight = Promise.allSettled(
      sectionIds.map(async (id) => {
        try {
          const html = await fetchSection(fetchUrl, id);
          swapSection(id, html);
        } catch (err) {
          // Per-section catch — keep existing DOM, never break the page.
          console.warn(`[product-page] section "${id}" re-render failed:`, err);
        }
      })
    );

    await inflight;
    document.dispatchEvent(new CustomEvent('product:section-swapped'));
  };

  document.addEventListener('change', (e) => {
    if (e.target.closest('variant-selects input[type="radio"]')) {
      handleVariantChange();
    }
  });

  // ─── Gallery thumbnail clicks ────────────────────────────────────────
  // Each thumb carries data-media-id matching one figure's data-media-id.
  // Click swaps the active media by toggling [hidden] on the figures and
  // the --active class on the thumbs.
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    const mediaId = thumb.dataset.mediaId;
    const gallery = thumb.closest('.product-gallery');
    if (!gallery) return;

    gallery.querySelectorAll('[data-gallery-thumb]').forEach((t) => {
      const isActive = t === thumb;
      t.classList.toggle('product-gallery__thumb--active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    gallery.querySelectorAll('[data-gallery-media]').forEach((m) => {
      m.hidden = m.dataset.mediaId !== mediaId;
    });
  });

  // ─── ATC submit interceptor ──────────────────────────────────────────
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    if (!window.MugCart || typeof window.MugCart.add !== 'function') {
      // Progressive enhancement — let the form POST to /cart/add traditionally.
      return;
    }
    e.preventDefault();

    const atc = form.querySelector('[data-product-atc]');
    const label = atc?.querySelector('[data-atc-label]');
    const originalLabel = label?.textContent;
    if (atc) {
      atc.disabled = true;
      if (label) label.textContent = 'ADDING…';
    }

    try {
      await window.MugCart.add(new FormData(form));
      // Drawer opens via mug-cart.js. Briefly flash success state.
      if (atc) atc.dataset.added = 'true';
      if (label) label.textContent = '✓ ADDED — VIEW CART';
      setTimeout(() => {
        if (atc) delete atc.dataset.added;
        if (label && originalLabel) label.textContent = originalLabel;
      }, 1800);
    } catch (err) {
      console.warn('[product-page] ATC failed:', err);
      // Surface notify-me with ATC-error context.
      setNotifyErrorContext(true);
      // Refresh sections so notify-me appears for the just-sold-out variant.
      handleVariantChange();
      // Scroll/focus the notify-me email input once it's in the DOM.
      requestAnimationFrame(() => {
        const input = document.querySelector('[data-notify-me-form] [name="email"]');
        if (input) {
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          input.focus();
        }
      });
    } finally {
      if (atc) atc.disabled = false;
    }
  });

  // ─── Sticky mobile ATC bar ───────────────────────────────────────────
  const initStickyAtc = () => {
    const atc = document.querySelector('[data-product-atc]');
    const stickyBar = document.querySelector('[data-sticky-atc]');
    if (!atc || !stickyBar) return;

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        stickyBar.hidden = e.intersectionRatio > 0;
      },
      { threshold: 0 }
    );
    io.observe(atc);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyAtc);
  } else {
    initStickyAtc();
  }

  // Re-init sticky-bar observer if the section was swapped.
  document.addEventListener('product:section-swapped', initStickyAtc);
})();
